/**
 * Shared chart primitives — colour palette, title, tooltip styling,
 * tick / value formatters.
 *
 * Lives in its own module so each per-type chart file (chart-bar.tsx,
 * chart-line.tsx, chart-area.tsx, chart-pie.tsx) imports just what
 * it needs and the bundler can split chunks per chart kind.
 */

// Series palette inspired by Grafana classic — saturated, distinct hues
// that read clearly against both light and dark backgrounds and avoid
// the muted-pastel feel of recharts defaults. Order is the canonical
// Grafana sequence so the first chart series is "primary blue" rather
// than a desaturated theme variable.
//
// Falls back to CSS custom properties first so a tenant can override
// the entire palette via `--chart-1`..`--chart-5` without touching code.
const DEFAULT_COLORS = [
  "hsl(var(--chart-1, 213 88% 65%))", // Grafana blue
  "hsl(var(--chart-2, 132 50% 58%))", // Grafana green
  "hsl(var(--chart-3, 26 95% 60%))", // Grafana orange
  "hsl(var(--chart-4, 354 82% 62%))", // Grafana red
  "hsl(var(--chart-5, 285 60% 65%))", // Grafana purple
  "hsl(var(--chart-6, 51 95% 58%))", // Grafana yellow
  "hsl(var(--chart-7, 188 75% 55%))", // Grafana cyan
  "hsl(var(--chart-8, 12 90% 60%))", // Grafana red-orange
]

export function getColor(color: string | undefined, index: number): string {
  return color ?? DEFAULT_COLORS[index % DEFAULT_COLORS.length]!
}

/**
 * Pick a colour for a series, preferring (in order):
 *  1. The author's explicit colour from the spec
 *  2. A name-hashed palette slot — the same `seriesName` always lands
 *     on the same colour, so "Backoffice" reads as the same blue on
 *     every chart in a dashboard
 *  3. Falls back to the index-based slot when no name is provided
 *
 * Hashing uses djb2 — small, deterministic, well-distributed for
 * short ASCII / UTF-16 strings. Not cryptographic; we just want the
 * mapping to be stable across renders and reproducible across charts.
 */
export function colorForSeries(
  seriesName: string | undefined,
  fallbackIndex: number,
  explicitColor?: string
): string {
  if (explicitColor) return explicitColor
  if (!seriesName) {
    return DEFAULT_COLORS[fallbackIndex % DEFAULT_COLORS.length]!
  }
  // djb2 — return non-negative slot. Hash is purely off the name so
  // "Backoffice" lands on the same slot regardless of position; the
  // fallbackIndex is only consulted when no name is supplied (above).
  let hash = 5381
  for (let i = 0; i < seriesName.length; i++) {
    hash = (hash * 33) ^ seriesName.charCodeAt(i)
  }
  const slot = (hash >>> 0) % DEFAULT_COLORS.length
  return DEFAULT_COLORS[slot]!
}

export function ChartTitle({ title }: { title?: string }) {
  if (!title) return null
  return <h3 className="text-foreground mb-2 text-sm font-medium">{title}</h3>
}

/**
 * Shared tooltip props — every chart applies the same popover style.
 * Spread into recharts `<Tooltip {...sharedTooltipProps} />`.
 */
export const sharedTooltipProps = {
  contentStyle: {
    backgroundColor: "hsl(var(--popover))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "0.5rem",
  },
  formatter: tooltipFormatter,
} as const

/**
 * Compact number formatter for chart axes ("541M" instead of
 * "541524042"). Keeps Y-axis labels narrow enough to fit in the
 * default ~70px gutter on bar / line / area charts. Localised to
 * Swedish suffixes (k = tusen, M = miljon, Mdr = miljard) since the
 * dashboard is admin-curated for Swedish tenants.
 */
export function formatTickNumber(value: unknown): string {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return value == null ? "" : String(value)
  }
  const abs = Math.abs(value)
  if (abs >= 1_000_000_000) {
    return `${stripTrailingZero((value / 1_000_000_000).toFixed(1))} Mdr`
  }
  if (abs >= 1_000_000) {
    return `${stripTrailingZero((value / 1_000_000).toFixed(1))} M`
  }
  if (abs >= 1_000) {
    return `${stripTrailingZero((value / 1_000).toFixed(1))} k`
  }
  // Sub-thousand values: integer when whole, one decimal otherwise.
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

function stripTrailingZero(s: string): string {
  // "541.0" → "541", "541.5" → "541.5". Keeps tick labels tight.
  return s.replace(/\.0$/, "")
}

/**
 * Tooltip value formatter. Shows the full number with thousand
 * separators (Swedish locale: 541 524 042) so a hover always reveals
 * the exact figure, even when the axis label is abbreviated.
 */
function tooltipFormatter(value: unknown): [string, string?] {
  if (typeof value === "number" && Number.isFinite(value)) {
    return [value.toLocaleString("sv-SE")]
  }
  return [value == null ? "" : String(value)]
}

/**
 * Default Y-axis width that fits the longest label this formatter
 * produces ("999.9 Mdr"). Without an explicit width recharts uses
 * ~60px which clips abbreviated labels longer than ~5 characters.
 */
export const DEFAULT_YAXIS_WIDTH = 72

/**
 * Shared recharts ``syncId`` for time-series tiles in a dashboard.
 * When set on multiple charts they cooperate on hover: pointing at
 * X=February in one chart shows a vertical guideline + tooltip at
 * X=February in every other chart with the same syncId. Mirrors
 * Grafana's "shared crosshair" behaviour.
 *
 * Only applied to line + area (continuous X). Bar charts are usually
 * categorical and would sync by index, which produces misleading
 * "matching" crosshairs across unrelated category axes.
 *
 * Single string for now — every Dash dashboard syncs internally. If
 * an embedded dashboard ever needs to share a crosshair with the
 * surrounding page, we can lift this into a context.
 */
export const TIMESERIES_SYNC_ID = "dash-timeseries"

/**
 * Outline colour for chart marks. Series colours are model- or tenant-chosen and are often
 * pale (~1.2-1.4:1 on the white card), so strokes and shape edges get a foreground outline to
 * reach the 3:1 non-text contrast of WCAG 1.4.11 while each series keeps its own colour.
 */
export const CHART_OUTLINE = "hsl(var(--foreground))"

// ── per-series legend stats ─────────────────────────────────────────

export interface SeriesStats {
  min: number | null
  max: number | null
  avg: number | null
  last: number | null
}

/**
 * Compute min / max / avg / last for a single numeric series across
 * a chart's data array. Skips non-finite values; returns all-null
 * when no finite reading is found (caller renders nothing in that
 * case).
 */
export function computeSeriesStats(
  data: ReadonlyArray<Record<string, unknown>> | undefined,
  dataKey: string
): SeriesStats {
  if (!Array.isArray(data) || data.length === 0) {
    return { min: null, max: null, avg: null, last: null }
  }
  let min = Infinity
  let max = -Infinity
  let sum = 0
  let count = 0
  let last: number | null = null
  for (const row of data) {
    const raw = row?.[dataKey]
    const n =
      typeof raw === "number"
        ? raw
        : typeof raw === "string" && raw.trim() !== ""
          ? Number(raw)
          : NaN
    if (!Number.isFinite(n)) continue
    if (n < min) min = n
    if (n > max) max = n
    sum += n
    count += 1
    last = n
  }
  if (count === 0) {
    return { min: null, max: null, avg: null, last: null }
  }
  return { min, max, avg: sum / count, last }
}

/**
 * Build the per-series stats string Grafana legends show inline,
 * formatted via formatTickNumber so the chart and the legend agree on
 * units. Returns "" when no finite readings exist (caller drops the
 * trailing block entirely).
 */
export function renderSeriesStatsText(stats: SeriesStats): string {
  if (stats.last === null) return ""
  return [
    `last ${formatTickNumber(stats.last)}`,
    `avg ${formatTickNumber(stats.avg ?? 0)}`,
    `max ${formatTickNumber(stats.max ?? 0)}`,
    `min ${formatTickNumber(stats.min ?? 0)}`,
  ].join("  ")
}
